import React, { useMemo } from "react";
import { Badge, Box, HStack, Text, Tooltip, VStack } from "@chakra-ui/react";
import type { FlowContext, FlowStep } from "../flow/types";
import { PHASES } from "../flow/flowGraph";
import { formatDuration } from "../flow/estimateTime";
import { ACTOR_META } from "./statusMeta";

interface Props {
  steps: FlowStep[];
  targetId: string;
  targetLabel: string;
  ctx?: FlowContext;
}

const PlannedStepsPreview: React.FC<Props> = ({ steps, targetId, targetLabel, ctx }) => {
  const skipCount = steps.filter((s) => (ctx && s.shouldSkip ? s.shouldSkip(ctx) : null)).length;
  const grouped = useMemo(
    () =>
      PHASES.map((phase) => ({
        phase,
        steps: steps.filter((s) => phase.stepIds.includes(s.id)),
      })).filter((g) => g.steps.length),
    [steps]
  );

  let index = 0;

  return (
    <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={4}>
      <HStack justify="space-between" align="flex-start" mb={4} spacing={4}>
        <Box minW={0}>
          <Text fontWeight="700" fontSize="sm" color="gray.800">
            Planned steps
          </Text>
          <HStack mt={1} spacing={2} flexWrap="wrap">
            <Text fontSize="xs" color="gray.500">
              Stop at
            </Text>
            <Badge colorScheme="blue" variant="subtle" fontSize="10px" maxW="100%" whiteSpace="normal">
              {targetLabel}
            </Badge>
          </HStack>
        </Box>
        <HStack spacing={3} flexShrink={0} color="gray.500" fontSize="10px">
          <Text fontFamily="mono">{steps.length} steps</Text>
          {skipCount > 0 && <Text fontFamily="mono">{skipCount} will skip</Text>}
        </HStack>
      </HStack>

      <VStack align="stretch" spacing={5}>
        {grouped.map(({ phase, steps: phaseSteps }) => (
          <Box key={phase.id}>
            <HStack mb={2} justify="space-between" pl="44px">
              <Text fontSize="10px" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em" color="gray.500">
                {phase.label}
              </Text>
              <Text fontSize="10px" color="gray.400" fontFamily="mono">
                {phaseSteps.length}
              </Text>
            </HStack>

            <VStack align="stretch" spacing={0}>
              {phaseSteps.map((step, stepIdx) => {
                const i = index++;
                const skip = ctx && step.shouldSkip ? step.shouldSkip(ctx) : null;
                const isTarget = step.id === targetId;
                const isLast = i === steps.length - 1;
                const actor = ACTOR_META[step.actor];
                const [minMs, maxMs] = step.estimateMs;

                return (
                  <HStack key={step.id} align="stretch" spacing={0} opacity={skip ? 0.72 : 1}>
                    <VStack spacing={0} w="36px" flexShrink={0} pt="10px">
                      <Box
                        w="22px"
                        h="22px"
                        borderRadius="full"
                        bg={isTarget ? "blue.500" : skip ? "gray.100" : "white"}
                        borderWidth="2px"
                        borderColor={isTarget ? "blue.500" : skip ? "gray.300" : "gray.300"}
                        color={isTarget ? "white" : "gray.500"}
                        fontSize="10px"
                        fontFamily="mono"
                        fontWeight="700"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                        lineHeight="1"
                      >
                        {i + 1}
                      </Box>
                      {!isLast && (
                        <Box
                          flex={1}
                          w="2px"
                          bg={stepIdx === phaseSteps.length - 1 ? "transparent" : "gray.200"}
                          mt={1}
                          minH="12px"
                        />
                      )}
                    </VStack>

                    <Box
                      flex={1}
                      minW={0}
                      mb={isLast ? 0 : 2}
                      px={3}
                      py={2.5}
                      borderWidth="1px"
                      borderColor={isTarget ? "blue.200" : skip ? "gray.200" : "gray.100"}
                      borderStyle={skip ? "dashed" : "solid"}
                      bg={isTarget ? "blue.50" : skip ? "gray.50" : "white"}
                      borderRadius="md"
                    >
                      <HStack justify="space-between" align="flex-start" spacing={3}>
                        <Box minW={0} flex={1}>
                          <HStack spacing={2} flexWrap="wrap">
                            <Text fontSize="sm" fontWeight="600" color="gray.800" lineHeight="1.3">
                              {step.name}
                            </Text>
                            <Badge colorScheme={actor.colorScheme} variant="subtle" fontSize="9px">
                              {actor.label}
                            </Badge>
                            {step.kind === "event" && (
                              <Badge colorScheme="orange" variant="outline" fontSize="9px">
                                event
                              </Badge>
                            )}
                            {step.optional && (
                              <Badge colorScheme="gray" variant="outline" fontSize="9px">
                                optional
                              </Badge>
                            )}
                            {isTarget && (
                              <Badge colorScheme="blue" fontSize="9px">
                                target
                              </Badge>
                            )}
                            {skip && (
                              <Tooltip label={skip} openDelay={300} hasArrow>
                                <Badge colorScheme="gray" fontSize="9px">
                                  will skip
                                </Badge>
                              </Tooltip>
                            )}
                          </HStack>
                          <Text fontSize="xs" color={skip ? "gray.500" : "gray.600"} mt={1} noOfLines={2} lineHeight="1.45">
                            {skip ? skip : step.description}
                          </Text>
                        </Box>
                        {!skip && (
                          <Text fontSize="10px" color="gray.400" fontFamily="mono" flexShrink={0} whiteSpace="nowrap" pt="2px">
                            {minMs === maxMs ? formatDuration(minMs) : `${formatDuration(minMs)}–${formatDuration(maxMs)}`}
                          </Text>
                        )}
                      </HStack>
                    </Box>
                  </HStack>
                );
              })}
            </VStack>
          </Box>
        ))}
      </VStack>
    </Box>
  );
};

export default PlannedStepsPreview;
